import { useState, useMemo } from "react";
import { Link } from "wouter";
import { Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/dashboard/pagination";
import { PeopleTable, IProjectPerson } from "./people-table";

interface ProjectPeopleContentProps {
  name: string;
  salicNumber: string;
  proponent: string;
  cnpj: string;
  people: IProjectPerson[];
  onView?: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
}

export function ProjectPeopleContent({
  name,
  salicNumber,
  proponent,
  cnpj,
  people,
  onView,
  onEdit,
  onDelete,
}: ProjectPeopleContentProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter people based on search
  const filteredPeople = useMemo(() => {
    if (!searchTerm) return people;

    const search = searchTerm.toLowerCase();
    return people.filter(
      (person) =>
        person.name.toLowerCase().includes(search) ||
        person.email.toLowerCase().includes(search) ||
        person.phone.includes(search) ||
        person.role.toLowerCase().includes(search)
    );
  }, [people, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPeople = filteredPeople.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const displayedCount = paginatedPeople.length;
  const totalCount = filteredPeople.length;

  return (
    <div className="flex flex-col gap-[40px] items-start w-full">
      {/* Header Section */}
      <div className="flex gap-[612px] items-start w-full">
        <div className="flex flex-col gap-[8px] items-start w-[301px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[24px] text-white w-[383px]">
            Pessoas do Projeto - {name}
          </p>
          <div className="flex font-['Plus_Jakarta_Sans'] font-normal gap-[10px] items-start leading-[0] text-[#d1d1d1] text-[14px] whitespace-pre w-full">
            <p className="leading-normal">
              <span>Número Salic: </span>
              <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[#f6f6f6]">
                {salicNumber}
              </span>
            </p>
            <p className="leading-normal">
              <span>Produtora: </span>
              <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[#f6f6f6]">
                {proponent} | {cnpj}
              </span>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-[10px] items-center">
          {/* Search Input */}
          <div className="border border-[#5d5d5d] border-solid box-border flex gap-[90px] h-[50px] items-center px-[20px] py-[10px] rounded-[10px] w-[227px]">
            <input
              type="text"
              placeholder="Procurar..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="font-['Plus_Jakarta_Sans'] font-normal leading-normal bg-transparent outline-none text-[#f6f6f6] text-[16px] whitespace-pre flex-1"
            />
            <Search className="shrink-0 w-[24px] h-[24px] text-[#f6f6f6]" />
          </div>

          {/* Filter Button */}
          <button className="border border-[#5d5d5d] border-solid box-border flex gap-[20px] h-[50px] items-center px-[20px] py-[10px] rounded-[10px] w-[117px]">
            <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
              Filtro
            </p>
            <Filter className="shrink-0 w-[24px] h-[24px] text-[#f6f6f6]" />
          </button>

          {/* Ir para Cadastros Button */}
          <Link href="/cadastros">
            <Button className="bg-[#7846FF] box-border flex gap-[3px] h-[50px] items-center justify-center p-[10px] rounded-[10px] w-[161px] hover:bg-[#6835ee]">
              <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f5f2ff] text-[16px] whitespace-pre">
                Ir para Cadastros
              </p>
            </Button>
          </Link>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex flex-col gap-[24px] items-start w-[1442px]">
        <PeopleTable
          people={paginatedPeople}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />

        {/* Pagination Section */}
        <div className="flex gap-[962px] items-center w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#888888] text-[14px] whitespace-pre">
            Exibindo {displayedCount} de {totalCount} transações
          </p>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}
